import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';

import LoadingArea from '@hiredigital/ui/LoadingArea';
import MainLayout from '@components/Layout/Layout';
import EnterpriseHero from '@components/PageComponents/Hero/NewEnterpriseHero';
import NewEnterpriseFutureOfWork from '@components/PageComponents/NewEnterpriseFutureOfWork';

import { getUsers, mergeCardUsersData, limitUsersData } from '@helpers/users';
import { replaceJson } from '@helpers/utils';
import { getStoriesBySlug } from '@helpers/posts';

// import Styles from './New.module.scss';

const NewEnterpriseHowItWorks = dynamic(
  () => import('@components/PageComponents/NewEnterpriseHowItWorks'),
  { loading: () => <LoadingArea height={950} />, ssr: false }
);

const NewEnterpriseHireTheBest = dynamic(
  () => import('@components/PageComponents/NewEnterpriseHireTheBest'),
  { loading: () => <LoadingArea height={938} />, ssr: false }
);

const NewEnterpriseWhyChoose = dynamic(
  () => import('@components/PageComponents/NewEnterpriseWhyChoose'),
  { loading: () => <LoadingArea height={779} />, ssr: false }
);

const RightTriangle = dynamic(() => import('@components/Parallax/RightTriangle'), {
  ssr: false,
});

const LeftTriangle = dynamic(() => import('@components/Parallax/LeftTriangle'), {
  ssr: false,
});

const CallToAction = dynamic(() => import('@components/PageComponents/CallToAction/CallToAction'), {
  ssr: false,
});

const PostSection = dynamic(() => import('@components/PageComponents/PostSection/PostSection'), {
  ssr: false,
});

const EnterpriseCaseStudy = dynamic(
  () => import('@components/PageComponents/EnterpriseCaseStudy'),
  { ssr: false }
);

const NewEnterpriseBuildYourTeam = dynamic(
  () => import('@components/PageComponents/NewEnterpriseBuildYourTeam'),
  { ssr: false }
);

const ClientTestimonial = dynamic(() => import('@components/ClientTestimonial/ClientTestimonial'), {
  ssr: false,
});

const NewEnterprisePage = ({
  hero,
  head,
  benefits,
  capabilities,
  survey,
  metrics,
  footer,
  midCtaSection,
  customerNames,
  cardUsers,
  posts,
  postData,
  ...props
}) => {
  return (
    <MainLayout
      darkFooter
      footerData={footer}
      pageHead={head}
      variant={MainLayout.Variants.ENTERPRISE}>
      <EnterpriseHero
        customerNames={customerNames}
        users={[]}
        title={'Build Digital Teams with the Best Talent on Demand'}
        description={
          'Hire Digital offers world-class on demand talent and teams to enterprise customers, with the support and agility organizations need to get work done.'
        }
        brandCtaLinkTo={`/schedule-call`}
        brandCtaLabel={`Schedule a Consultation`}
      />
      <LeftTriangle />
      <NewEnterpriseFutureOfWork style={{ marginTop: '-350px' }} />
      <NewEnterpriseHowItWorks />
      <NewEnterpriseHireTheBest />
      <RightTriangle />

      <NewEnterpriseWhyChoose />
      <EnterpriseCaseStudy />
      <LeftTriangle />

      <NewEnterpriseBuildYourTeam />
      <ClientTestimonial isEnterprise />
      <RightTriangle />

      <PostSection
        isEnterprise
        title='Read Hire Digital Stories'
        posts={posts}
        postData={postData}
      />

      <CallToAction isEnterprise />
    </MainLayout>
  );
};

NewEnterprisePage.propTypes = {
  hero: PropTypes.object,
  head: PropTypes.object,
  footer: PropTypes.object,
  midCtaSection: PropTypes.object,
  customerNames: PropTypes.array,
  cardUsers: PropTypes.array,
  posts: PropTypes.array,
  postData: PropTypes.array,
};
export default NewEnterprisePage;

export const getStaticProps = async (context) => {
  const pageData = replaceJson(require('../../../data/pages/enterprise.json'));

  const { users = [], ...restPageData } = pageData;
  const jsonUsers = [];
  const userUuids = users.filter(({ uuid }) => !!uuid).map(({ uuid }) => uuid);
  let apiUsers = [];
  if (userUuids.length) {
    apiUsers = await getUsers(userUuids);
  }

  const posts = pageData.posts;
  const articles = posts.map(({ slug }) => slug);
  const postData = await getStoriesBySlug(articles);

  const cardUsers = limitUsersData(mergeCardUsersData(jsonUsers, users, apiUsers));

  return {
    props: { ...restPageData, cardUsers, postData },
    revalidate: 500,
  };
};
